import React, { useEffect, useState } from 'react';
import PayLaterMessage from './PayLaterMessage';
import { Card, Table, Form, Overlay, Popover } from "react-bootstrap";
import { useSelector } from 'react-redux';
import { useRef } from 'react';
import Favourite from "../components/Favourite";
import { calculateDiscountedPrice } from "../utils/discountUtils";
import { isEtaPassed } from "../utils/etaUtils";
import {
    useGetNotifyStatusQuery,
    useAddToNotifyListMutation,
    useRemoveFromNotifyListMutation
} from "../slices/productsApiSlice";
import { toast } from "react-toastify";
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { addToCart } from '../slices/cartSlice';

const ProductDetails = ({
                            product,
                            selectedSize,
                            setSelectedSize,
                            selectedColour,
                            setSelectedColour,
                            isInStock,
                            setIsInStock,
                            qty,
                            setQty,
                            addToCartHandler,
                            productId,
                            showDiscount
                        }) => {
    const discount = product?.discount ?? 0;
    const discountedPrice = calculateDiscountedPrice(product?.price ?? 0, discount);

    const isMobile = window.innerWidth <= 430;

    const dispatch = useDispatch();
    const navigate = useNavigate();

    // A date that has gone by tells the customer nothing, so it's treated as
    // "no date known": it isn't shown and pre-orders close until it's updated.
    const etaPassed = isEtaPassed(product?.eta);
    const canPreOrder = !!product?.preOrderAvailable && !etaPassed;

    const isWithin28Days = product?.eta
        ? (new Date(product.eta) - new Date()) / (1000 * 60 * 60 * 24) <= 28
        : false;

    const { userInfo } = useSelector((state) => state.auth);

    const [showPopover, setShowPopover] = useState(false);
    const [showStickyBar, setShowStickyBar] = useState(false);
    const mobileActionsRef = useRef(null);

    // Keep a buy button on screen whenever the main one is out of view
    useEffect(() => {
        const target = mobileActionsRef.current;
        if (!target || !('IntersectionObserver' in window)) return undefined;
        const observer = new IntersectionObserver(([entry]) => {
            setShowStickyBar(!entry.isIntersecting);
        });
        observer.observe(target);
        return () => observer.disconnect();
    }, []);
    const popoverTarget = useRef(null);

    const [addToNotifyList, { isLoading: notifyLoading }] = useAddToNotifyListMutation();
    const [removeFromNotifyList, { isLoading: removeNotifyLoading }] = useRemoveFromNotifyListMutation();
    // const [createPreOrder, { isLoading: preOrderLoading }] = useCreatePreOrderMutation();

    const { data: notifyStatus } = useGetNotifyStatusQuery(
        { productId: product?._id, userId: userInfo?._id },
        { skip: !userInfo || !product?._id || product.countInStock !== 0 }
    );
    const isOnNotifyList = !!notifyStatus?.onList;

    const notifyHandler = async () => {
        if (!userInfo) {
            toast.error('Please sign in to be notified');
            return;
        }
        try {
            if (isOnNotifyList) {
                await removeFromNotifyList(product._id).unwrap();
                toast.success('Notification removed');
            } else {
                await addToNotifyList(product._id).unwrap();
                toast.success(`Hi ${userInfo.name.split(' ')[0]}! We'll notify you when this is back in stock!`);
            }
        } catch (err) {
            toast.error(err?.data?.message || 'Something went wrong');
        }
    };

    const preOrderHandler = () => {
        if (!userInfo) {
            toast.error('Please sign in to pre-order');
            return;
        }
        const cartItem = {
            ...product,
            qty: 1,
            isPreOrder: true,
            eta: product.eta,
        };
        dispatch(addToCart(cartItem));
        navigate('/cart');
    };

    const formattedEta = product?.eta && !etaPassed
        ? new Date(product.eta).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        })
        : null;

    // Out of stock but open for pre-order — Clearpay can't be offered for these
    const isPreOrderOnly = !!product && product.countInStock <= 0 && canPreOrder;

    const rows = [
        {
            label: 'Price:',
            value: showDiscount && product.isClearance && discount > 0 ? (
                <>
                    <span style={{ textDecoration: 'line-through' }}>£{product.price.toFixed(2)}</span>
                    <br />
                    <span>Now: £{discountedPrice.toFixed(2)}</span>
                    <br />
                    <span>Save {discount}%</span>
                    <PayLaterMessage price={discountedPrice} isPreOrder={isPreOrderOnly} />
                </>
            ) : (
                <>
                    £{product.price.toFixed(2)}
                    <PayLaterMessage price={product.price} isPreOrder={isPreOrderOnly} />
                </>
            )
        },
        {
            label: 'Status:',
            value: (
                <strong style={{ color: (product.countInStock !== null && product.countInStock > 0) ? 'green' : 'orange' }}>
                    {(product.countInStock !== null && product.countInStock > 0) ? 'In Stock' : 'Awaiting Stock'}
                </strong>
            )
        }
    ];

    if (product && (product.countInStock > 0 || (product.productType === 'Classic Tees' && product.sizeArray?.some(s => s.tshirtInStock > 0)) || (product.productType === 'Hats' && product.colourArray?.some(c => c.colourInStock > 0)))) {
        if (product.productType === 'Classic Tees' && product.sizeArray && product.sizeArray.length > 0) {
            rows.push({
                label: 'Size:',
                value: (
                    <Form.Control
                        as='select'
                        value={selectedSize}
                        onChange={(e) => {
                            const size = e.target.value;
                            setSelectedSize(size);
                            const sizeDetails = product.sizeArray.find(s => s.size === size);
                            if (sizeDetails) {
                                setIsInStock(sizeDetails.tshirtInStock > 0);
                            }
                        }}
                        style={{ display: 'block', margin: '0 auto', textAlign: 'center', textAlignLast: 'center' }}
                        aria-label="Select product size"
                    >
                        <option value=''>Select a size</option>
                        {product.sizeArray.map((sizeInfo) => (
                            <option
                                key={sizeInfo._id}
                                value={sizeInfo.size}
                                disabled={sizeInfo.tshirtInStock === 0 || sizeInfo.tshirtInStock === null}
                            >
                                {sizeInfo.size}
                                {sizeInfo.tshirtInStock > 0 ? '' : ' (Awaiting Stock)'}
                            </option>
                        ))}
                    </Form.Control>
                )
            });
        }

        if (product.productType === 'Hats' && product.colourArray && product.colourArray.length > 0) {
            rows.push({
                label: 'Colour:',
                value: (
                    <Form.Control
                        as='select'
                        value={selectedColour}
                        onChange={(e) => {
                            const colour = e.target.value;
                            setSelectedColour(colour);
                            const colourDetails = product.colourArray.find(c => c.colour === colour);
                            if (colourDetails) {
                                setIsInStock(colourDetails.colourInStock > 0);
                            }
                        }}
                        style={{ display: 'block', margin: '0 auto', textAlign: 'center', textAlignLast: 'center' }}
                        aria-label="Select product colour"
                    >
                        <option value=''>Select a colour</option>
                        {product.colourArray.map((colourInfo) => (
                            <option
                                key={colourInfo._id}
                                value={colourInfo.colour}
                                disabled={colourInfo.colourInStock === 0 || colourInfo.colourInStock === null}
                            >
                                {colourInfo.colour}
                                {colourInfo.colourInStock > 0 ? '' : ' (Awaiting Stock)'}
                            </option>
                        ))}
                    </Form.Control>
                )
            });
        }

        const showQtySelector = product.productType === 'Classic Tees'
            ? (selectedSize !== '' && isInStock)
            : product.productType === 'Hats'
                ? (selectedColour !== '' && isInStock)
                : product.countInStock > 0;

        if (showQtySelector) {
            let maxQty = 10;
            if (product.productType === 'Classic Tees') {
                maxQty = Math.min(10, product.sizeArray.find(s => s.size === selectedSize)?.tshirtInStock || 10);
            } else if (product.productType === 'Hats') {
                maxQty = Math.min(10, product.colourArray.find(c => c.colour === selectedColour)?.colourInStock || 10);
            } else {
                maxQty = Math.min(10, product.countInStock);
            }

            rows.push({
                label: 'Qty:',
                value: (
                    <div className="centered-select">
                        <select
                            value={qty}
                            onChange={(e) => setQty(Number(e.target.value))}
                            style={{ width: '100%', textAlignLast: 'center' }}
                            aria-label="Select quantity"
                        >
                            {[...Array(maxQty).keys()].map(x => (
                                <option key={x + 1} value={x + 1}>{x + 1}</option>
                            ))}
                        </select>
                    </div>
                )
            });
        }
    }

    const isEvenRows = rows.length % 2 === 0;

    let isDisabled = false;
    let disabledReason = '';

    if (product.productType === 'Classic Tees') {
        isDisabled = !isInStock || selectedSize === '';
        disabledReason = selectedSize === '' ? 'Please select a size first' : 'Selected size out of stock';
    } else if (product.productType === 'Hats') {
        isDisabled = !isInStock || selectedColour === '';
        disabledReason = selectedColour === '' ? 'Please select a colour first' : 'Selected colour out of stock';
    } else {
        isDisabled = product.countInStock === 0;
        disabledReason = 'Product out of stock';
    }

    const outOfStock = product.countInStock === 0;

    // Shared button style — matches Add To Cart exactly
    const addToCartStyle = (disabled) => ({
        background: disabled
            ? 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
            : 'linear-gradient(135deg, #3E6D9C 0%, #2d5373 100%)',
        border: '2px solid #3E6D9C',
        whiteSpace: 'nowrap',
        borderRadius: '50px',
        // padding: '12px 24px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '10px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        transition: 'all 0.3s ease',
        fontWeight: '600',
        // fontSize: '14px',
        color: 'white',
        boxShadow: disabled ? 'none' : '0 4px 12px rgba(62, 109, 156, 0.3)',
        minWidth: isMobile ? '110px' : '160px',
        maxWidth: isMobile ? '110px' : '160px',
        padding: isMobile ? '8px 10px' : '12px 24px',
        fontSize: isMobile ? '12px' : '14px',
        justifyContent: 'center'
    });

    // Wishlist-style outline button for Pre-Order
    const outlineButtonStyle = {
        background: 'transparent',
        border: '2px solid #c3cfe2',
        borderRadius: '50px',
        // padding: '12px 24px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '10px',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        fontWeight: '600',
        // fontSize: '14px',
        color: '#555',
        minWidth: isMobile ? '110px' : '160px',
        maxWidth: isMobile ? '110px' : '160px',
        padding: isMobile ? '8px 10px' : '12px 24px',
        fontSize: isMobile ? '12px' : '14px',
        justifyContent: 'center'
    };

    return (
        <Card className="my-3">
            <Card.Body>
                <Table bordered responsive style={{ boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)', borderRadius: '10px', overflow: 'hidden' }}>
                    <tbody>
                    {rows.map((row, index) => (
                        <tr key={index} style={{ backgroundColor: (index + (isEvenRows ? 0 : 1)) % 2 === 0 ? '#ffffff' : '#e3f2fd' }}>
                            <td style={{ verticalAlign: 'middle', textAlign: 'center', color: 'black' }}>{row.label}</td>
                            <td style={{ color: 'black', textAlign: 'center', verticalAlign: 'middle' }}>{row.value}</td>
                        </tr>
                    ))}
                    {!isMobile && <tr style={{ backgroundColor: (rows.length + (isEvenRows ? 0 : 1)) % 2 === 0 ? '#ffffff' : '#e3f2fd' }}>
                        <td className="text-center" style={{ padding: '20px 0' }}>
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                <Favourite productId={productId} />
                            </div>
                        </td>
                        <td className="text-center" style={{ padding: '20px 0' }}>
                            {/* Add To Cart — always visible, disabled when out of stock */}
                            <button
                                onClick={addToCartHandler}
                                type="button"
                                disabled={isDisabled}
                                aria-label={isDisabled ? disabledReason : 'Add product to shopping basket'}
                                style={addToCartStyle(isDisabled)}
                                onMouseEnter={(e) => {
                                    if (!isDisabled) {
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                        e.currentTarget.style.boxShadow = '0 6px 16px rgba(62, 109, 156, 0.4)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!isDisabled) {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(62, 109, 156, 0.3)';
                                    }
                                }}
                            >
                                Add To Basket
                            </button>
                        </td>
                    </tr>}
                    </tbody>
                </Table>

                {/* Phones: full-width buy button, and a sticky bar once it scrolls away */}
                {isMobile && (
                    <div className="pd-mobile-actions" ref={mobileActionsRef}>
                        <button
                            onClick={addToCartHandler}
                            type="button"
                            disabled={isDisabled}
                            aria-label={isDisabled ? disabledReason : 'Add product to shopping basket'}
                            style={{ ...addToCartStyle(isDisabled), minWidth: '100%', maxWidth: '100%', minHeight: '50px', fontSize: '16px' }}
                        >
                            {isDisabled && !outOfStock ? disabledReason : 'Add To Basket'}
                        </button>
                        <Favourite productId={productId} />
                    </div>
                )}
                {isMobile && !outOfStock && showStickyBar && (
                    <div className="pd-sticky-bar">
                        <strong>£{(showDiscount && product.isClearance && discount > 0 ? discountedPrice : product.price).toFixed(2)}</strong>
                        <button
                            type="button"
                            style={{ ...addToCartStyle(false), minWidth: 0, maxWidth: 'none', flex: 1, minHeight: '46px', fontSize: '15px' }}
                            onClick={isDisabled
                                ? () => mobileActionsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
                                : addToCartHandler}
                        >
                            {isDisabled ? 'Choose Options' : 'Add To Basket'}
                        </button>
                    </div>
                )}

                {/* Out of stock actions — below table, same button styles as above */}
                {outOfStock && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center', padding: '16px 0 8px 0' }}>

                        {/* ETA */}
                        {formattedEta && (
                            <div style={{ color: '#FD7014', fontWeight: '600', fontSize: '15px' }}>
                                <i className="fa-regular fa-calendar-clock" style={{ marginRight: '8px' }} />
                                Expected {formattedEta}
                            </div>
                        )}

                        {/* Not signed in message */}
                        {!userInfo ? (
                            <p style={{ color: '#555', fontSize: '14px', margin: 0 }}>
                                <Link to='/login' style={{ color: '#3E6D9C', fontWeight: '600' }}>Sign in</Link> to get notified{canPreOrder ? ' or place a pre-order' : ''}
                            </p>
                        ) : (
                            /* Notify Me and Pre-Order side by side */
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>

                                {/* Notify Me */}
                                <button
                                    onClick={notifyHandler}
                                    disabled={notifyLoading || removeNotifyLoading}
                                    type="button"
                                    style={addToCartStyle(false)}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                        e.currentTarget.style.boxShadow = '0 6px 16px rgba(62, 109, 156, 0.4)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(62, 109, 156, 0.3)';
                                    }}
                                >
                                    <i className={`fa-regular ${isOnNotifyList ? 'fa-bell-slash' : 'fa-bell'}`} />
                                    {isOnNotifyList ? 'Remove' : 'Notify Me'}
                                </button>

                                {/* Pre-Order */}
                                {canPreOrder && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <button
                                            onClick={preOrderHandler}
                                            disabled={!isWithin28Days}
                                            type="button"
                                            style={{
                                                ...outlineButtonStyle,
                                                opacity: isWithin28Days ? 1 : 0.5,
                                                cursor: isWithin28Days ? 'pointer' : 'not-allowed',
                                            }}
                                            onMouseEnter={(e) => {
                                                if (isWithin28Days) e.currentTarget.style.borderColor = '#3E6D9C';
                                            }}
                                            onMouseLeave={(e) => {
                                                if (isWithin28Days) e.currentTarget.style.borderColor = '#3E6D9C';
                                            }}
                                        >
                                            <i className="fa-regular fa-box" />
                                            Pre-Order
                                        </button>
                                        <span
                                            ref={popoverTarget}
                                            onClick={() => setShowPopover(!showPopover)}
                                            style={{ cursor: 'pointer', color: '#3E6D9C' }}
                                            title="Pre-order information"
                                        >
            <i className="fa-regular fa-circle-info" style={{ fontSize: '18px' }} />
        </span>
                                        <Overlay
                                            target={popoverTarget.current}
                                            show={showPopover}
                                            placement="top"
                                            rootClose
                                            onHide={() => setShowPopover(false)}
                                        >
                                            <Popover id="preorder-info-popover">
                                                <Popover.Header as="h3">About Pre-Orders</Popover.Header>
                                                <Popover.Body>
                                                    {!isWithin28Days ? (
                                                        <p style={{ margin: 0, fontSize: '0.9rem', color: '#888' }}>
                                                            Pre-order is currently unavailable as the expected arrival is more than 28 days away. Use <strong>Notify Me</strong> instead and we'll let you know when it's in stock.
                                                        </p>
                                                    ) : (
                                                        <ul style={{ paddingLeft: '1.2rem', margin: 0, fontSize: '0.9rem' }}>
                                                            <li>No payment taken until your order is dispatched</li>
                                                            <li>We'll keep you updated on progress and any delays</li>
                                                            <li>Cancel any time before dispatch — no questions asked</li>
                                                            <li>You'll be first in line when stock arrives</li>
                                                        </ul>
                                                    )}
                                                </Popover.Body>
                                            </Popover>
                                        </Overlay>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </Card.Body>
        </Card>
    );
};

export default ProductDetails;